import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState } from "react";
import LandingRichText from "./LandingRichText";
import { checkIfContentEmpty } from "../../utils/tiptap";
import { LANDING_BRAND } from "./landingBrand";

type FAQItem = {
  question?: string;
  answer?: string;
};

type FAQHeader = {
  badge?: string;
  content?: string;
};

type Props = {
  header?: FAQHeader;
  items?: FAQItem[];
};

const hasHeaderContent = (header?: FAQHeader) => {
  if (header?.badge?.trim()) {
    return true;
  }
  return Boolean(header?.content?.trim() && !checkIfContentEmpty(header.content));
};

const FAQSection = ({ header, items }: Props) => {
  const [expanded, setExpanded] = useState<string | false>(false);
  const showHeader = hasHeaderContent(header);
  const faqItems = items?.filter(
    (item) => item?.question?.trim() && item?.answer?.trim(),
  );

  if (!showHeader && !faqItems?.length) {
    return null;
  }

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  return (
    <Box
      component="section"
      sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fff" }}
    >
      <Container sx={{
        maxWidth: {
          xs: "100%",
          md: "60%",
        },
      }}>
        {showHeader && (
          <Box sx={{ textAlign: "center", mb: 6 }}>
            {header?.badge?.trim() && (
              <Typography
                variant="body1"
                fontWeight="regular"
                sx={{
                  display: "inline-block",
                  mb: 2,
                  p: "4px 16px",
                  borderRadius: 999,
                  bgcolor: LANDING_BRAND.primaryLight,
                  color: LANDING_BRAND.primaryDark,
                  lineHeight: "1.25rem",
                }}
              >
                {header.badge}
              </Typography>
            )}
            <LandingRichText
              html={header?.content}
              sx={{
                fontSize: "1.25rem",
                color: "rgb(75, 85, 99)",
                lineHeight: "1.75rem",
                // maxWidth: "48rem",
                mx: "auto",
                textAlign: "center",
                "& p": { margin: "0 0 0.75rem", color: "inherit" },
                "& p:last-child": { marginBottom: 0 },
                "& h1, & h2, & h3": {
                  fontWeight: "bold",
                  color: "text.primary",
                  mb: 2,
                },
                "& h1": {
                  fontSize: { xs: "1.875rem", md: "2.25rem" },
                  lineHeight: "2.5rem",
                },
                "& h2": {
                  fontSize: { xs: "1.5rem", md: "1.875rem" },
                  lineHeight: "2.25rem",
                },
                "& h3": {
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                  lineHeight: "2rem",
                },
                "& ul, & ol": {
                  margin: "0.5rem auto",
                  paddingLeft: "1.5rem",
                  textAlign: "left",
                  display: "inline-block",
                },
                "& a": { color: LANDING_BRAND.primaryDark },
              }}
            />
          </Box>
        )}

        {faqItems?.map((faq, index) => (
          <Accordion
            key={index}
            disableGutters
            TransitionProps={{ unmountOnExit: true }}
            expanded={expanded === `panel${index}`}
            onChange={handleChange(`panel${index}`)}
            sx={{
              boxShadow: "none",
              borderBottom: "1px solid #E5E7EB",
              borderRadius: "8px !important",
              mb: 2.5,
              "::before": {
                backgroundColor: "transparent",
              },
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{
                    color: "text.primary",
                  }}
                />
              }
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: "regular",
                  lineHeight: "1.75rem",
                }}
              >
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="body1"
                sx={{
                  color: "#374151",
                  lineHeight: 1.5,
                }}
              >
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Container>
    </Box>
  );
};

export default FAQSection;
